import React, { useEffect, useState } from "react";
import { Box, Typography, alpha, useTheme, Divider } from "@mui/material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import GavelIcon from "@mui/icons-material/Gavel";
import PrivacyTipOutlinedIcon from "@mui/icons-material/PrivacyTipOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { ProfileForm } from "./ProfileForm";
import { SecurityForm } from "./security-form";
import { FinancesForm } from "./finances-form";
import { PayoutForm } from "./payout-form";
import { PaymentHistoryForm } from "./payment-history-form";
import { useSearchParams, Link } from "react-router-dom";

type TabName = "profile" | "finances" | "security" | "history";

const tabs = [
  { name: "profile",   label: "Profile",         Icon: PersonOutlineIcon },
  { name: "security",  label: "Security",         Icon: LockOutlinedIcon },
  { name: "finances",  label: "Finances",         Icon: AccountBalanceIcon },
  { name: "history",   label: "Payment History",  Icon: ReceiptLongIcon },
] as const;

export default function SettingsPage() {
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get("tab") as TabName;
  const [activeTab, setActiveTab] = useState<TabName>("profile");

  useEffect(() => {
    if (tabParam && ["profile", "finances", "security", "history"].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [tabParam]);

  return (
    <Box sx={{ maxWidth: 1100, mx: "auto", px: { xs: 2, sm: 3 }, py: { xs: 3, sm: 4 } }}>
      {/* Page header */}
      <Box sx={{ mb: 4 }}>
        <Typography sx={{ fontSize: "1.5rem", fontWeight: 800, color: "text.primary", letterSpacing: "-0.01em" }}>
          Settings
        </Typography>
        <Typography sx={{ fontSize: "0.875rem", color: "text.secondary", mt: 0.5 }}>
          Manage your account settings and preferences.
        </Typography>
      </Box>

      <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 3, alignItems: "flex-start" }}>
        {/* Sidebar nav */}
        <Box
          sx={{
            width: { xs: "100%", sm: 220 },
            flexShrink: 0,
            borderRadius: 4,
            bgcolor: "background.paper",
            boxShadow: "0 1px 3px rgba(16,24,64,0.06), 0 4px 16px rgba(16,24,64,0.08)",
            p: 1.5,
          }}
        >
          <Typography
            sx={{
              fontSize: "0.78rem",
              fontWeight: 700,
              color: "text.disabled",
              textTransform: "uppercase",
              letterSpacing: "0.12em",
              px: 1.25,
              pt: 0.25,
              pb: 1,
            }}
          >
            Account
          </Typography>

          {tabs.map(({ name, label, Icon }) => {
            const isActive = activeTab === name;
            return (
              <Box
                key={name}
                onClick={() => setActiveTab(name)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  px: 1.5,
                  py: 1,
                  borderRadius: 2,
                  cursor: "pointer",
                  mb: 0.25,
                  bgcolor: isActive ? alpha(theme.palette.primary.main, 0.08) : "transparent",
                  transition: "background-color 0.15s",
                  "&:hover": {
                    bgcolor: isActive
                      ? alpha(theme.palette.primary.main, 0.08)
                      : alpha(theme.palette.text.secondary, 0.06),
                  },
                }}
              >
                <Icon
                  sx={{
                    fontSize: "1.1rem",
                    color: isActive ? "primary.main" : "text.secondary",
                    flexShrink: 0,
                  }}
                />
                <Typography
                  sx={{
                    fontSize: "0.875rem",
                    fontWeight: isActive ? 600 : 400,
                    color: isActive ? "primary.main" : "text.primary",
                  }}
                >
                  {label}
                </Typography>
              </Box>
            );
          })}

          <Divider sx={{ my: 1.5, mx: 0.5 }} />

          <Typography
            sx={{
              fontSize: "0.78rem",
              fontWeight: 700,
              color: "text.disabled",
              textTransform: "uppercase",
              letterSpacing: "0.12em",
              px: 1.25,
              pb: 1,
            }}
          >
            Legal & Resources
          </Typography>

          {[
            { label: "How It Works", to: "/how-it-works", Icon: InfoOutlinedIcon },
            { label: "Terms of Service", to: "/terms", Icon: GavelIcon },
            { label: "Privacy Policy", to: "/privacy", Icon: PrivacyTipOutlinedIcon },
            { label: "Disclaimer", to: "/disclaimer", Icon: WarningAmberIcon },
          ].map(({ label, to, Icon }) => (
            <Box
              key={to}
              component={Link}
              to={to}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                px: 1.5,
                py: 1,
                borderRadius: 2,
                mb: 0.25,
                textDecoration: "none",
                color: "text.primary",
                transition: "background-color 0.15s",
                "&:hover": {
                  bgcolor: alpha(theme.palette.text.secondary, 0.06),
                },
              }}
            >
              <Icon sx={{ fontSize: "1.1rem", color: "text.secondary", flexShrink: 0 }} />
              <Typography sx={{ fontSize: "0.875rem", fontWeight: 400, flex: 1 }}>
                {label}
              </Typography>
              <OpenInNewIcon sx={{ fontSize: "0.75rem", color: "text.disabled" }} />
            </Box>
          ))}
        </Box>

        {/* Content area */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {activeTab === "profile"  && <ProfileForm />}
          {activeTab === "security" && <SecurityForm />}
          {activeTab === "finances" && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <FinancesForm />
              <PayoutForm />
            </Box>
          )}
          {activeTab === "history"  && <PaymentHistoryForm />}
        </Box>
      </Box>
    </Box>
  );
}
